import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import {
  ICustomProgram,
  ICustomSchedule,
  extractRotatingQuestions
} from '../../../libs/composer';

interface RotatingQuestionsSummaryProps {
  customProgram: ICustomProgram;
  schedule: ICustomSchedule;
  params: {
    patient: number;
    programId: number | null;
  };
}

const RotatingQuestionsSummary: React.FC<RotatingQuestionsSummaryProps> = ({
  customProgram,
  schedule,
  params: _params
}): JSX.Element | null => {
  // Extract rotating questions from schema
  const questions = useMemo(() => {
    if (!customProgram?.source) {
      return [];
    }
    try {
      return extractRotatingQuestions(customProgram.source);
    } catch {
      return [];
    }
  }, [customProgram?.source]);

  // Get responses from schedule config
  const responses = useMemo(() => {
    if (!schedule?._config?.rotatingQuestionResponses) {
      return [];
    }
    return schedule._config.rotatingQuestionResponses;
  }, [schedule?._config?.rotatingQuestionResponses]);

  // Get current rotation state
  const currentIndex = schedule?._config?.rotatingQuestionIndex ?? 0;
  const nextQuestionIndex = currentIndex % (questions.length || 1);

  if (questions.length === 0) {
    return null;
  }

  // Format response value
  const formatResponse = (value: boolean | string | number): string => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  return (
    <div>
      <div className="mb-3">
        <strong>Rotation Status:</strong> Next question: Q
        {nextQuestionIndex + 1} | Questions asked: {responses.length}/
        {questions.length}
      </div>
      <table className="table table-striped">
        <thead>
          <tr>
            <th>Question #</th>
            <th>Question Text</th>
            <th>Response</th>
            <th>Date/Time</th>
          </tr>
        </thead>
        <tbody>
          {responses.length > 0 ? (
            responses.map((response, idx) => {
              const question = questions[response.questionIndex];
              return (
                <tr key={idx}>
                  <td>Q{response.questionIndex + 1}</td>
                  <td>
                    {question?.text || `Question ${response.questionIndex + 1}`}
                  </td>
                  <td>{formatResponse(response.response)}</td>
                  <td>
                    {response.timestamp
                      ? moment(response.timestamp).format('MM/DD/YYYY HH:mm')
                      : 'N/A'}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={4} className="text-center text-muted">
                No responses yet
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RotatingQuestionsSummary;
